import { Spin } from 'antd'
import { useCdfRawValues } from 'features/dataLayers'
import { CriticalProfilePoint } from 'features/indicationTriage/cards/criticalProfile/criticalProfileApi'
import {
  computeCriticalPath,
  computeLinearInterpolation,
  computeSegmentArea,
  computeSlices,
  CtpSlice,
} from 'features/indicationTriage/cards/ffsCalc/calculations/calculateCtp'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_L,
  LABEL_Q,
  LABEL_R_T,
  LABEL_RSFA,
  LABEL_TC_AM,
  LABEL_TS_AM,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import {
  computeLength,
  computeLengthFactor,
} from 'features/indicationTriage/cards/ffsCalc/lengthFactorLookup'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import isNil from 'lodash/isNil'
import minBy from 'lodash/minBy'
import reduce from 'lodash/reduce'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { FfsAverageThicknessModal } from './FfsAverageThicknessModal'

const TOOLTIP_R_T = (
  <>
    Remaining Thickness Ratio
    <TooltipDivider />R<Sub>t</Sub> = (t<Sub>mm</Sub> - FCA<Sub>ml</Sub>) / t
    <Sub>ml</Sub>
  </>
)

function minIndexBy<T>(
  values: ReadonlyArray<T>,
  extractor: (value: T) => number
): number {
  // Default to -1 if no values are provided
  if (values.length <= 0) return -1

  return reduce(
    values,
    (minIdx, point, idx, arr) =>
      extractor(point) < extractor(arr[minIdx]) ? idx : minIdx,
    0
  )
}

/**
 * Computes the excess area between a bound and the edge of a slice.
 * Handles two scenarios:
 * 1. Neighbor exists: Uses linear interpolation and trapezoid area
 * 2. No neighbor (edge of critical path): Uses rectangular area with tc
 */
function computeExcessArea(
  boundX: number,
  edgePoint: CriticalProfilePoint,
  neighborPoint: CriticalProfilePoint | undefined,
  axis: 'x' | 'y',
  tc: GeckoMeasurement
): number {
  const edgeX = edgePoint.coords[axis].toNumber(WORKING_UNIT_THICKNESS)
  const edgeY =
    edgePoint.value?.toNumber(WORKING_UNIT_THICKNESS) ??
    tc.toNumber(WORKING_UNIT_THICKNESS)

  if (neighborPoint) {
    // Scenario 1: Neighbor exists - use linear interpolation
    const neighborX = neighborPoint.coords[axis].toNumber(
      WORKING_UNIT_THICKNESS
    )
    const neighborY =
      neighborPoint.value?.toNumber(WORKING_UNIT_THICKNESS) ??
      tc.toNumber(WORKING_UNIT_THICKNESS)

    const boundThickness = computeLinearInterpolation(
      edgeX,
      edgeY,
      neighborX,
      neighborY,
      boundX
    )

    return computeSegmentArea(edgeX, edgeY, boundX, boundThickness)
  } else {
    // Scenario 2: No neighbor (edge of critical path) - rectangular area
    const width = Math.abs(boundX - edgeX)
    return width * tc.toNumber(WORKING_UNIT_THICKNESS)
  }
}

// Lv. 1, Step 5 Implementation
// Refer to API 579, Figure 4.19
function computeCtpAverage(
  criticalPath: ReadonlyArray<CriticalProfilePoint>,
  axis: 'x' | 'y',
  l: GeckoMeasurement | null,
  tc: GeckoMeasurement | null,
  fcaMl: GeckoMeasurement | null,
  d_ml: GeckoMeasurement | null
): GeckoMeasurement | null {
  if (!l || !tc || !fcaMl || !d_ml || criticalPath.length <= 0) return null

  // Find minimum thickness point index
  const minPointIdx: number = minIndexBy(
    criticalPath,
    (point) =>
      point.value?.toNumber(WORKING_UNIT_THICKNESS) ?? Number.POSITIVE_INFINITY
  )

  if (minPointIdx < 0) return null

  const minPoint = criticalPath[minPointIdx]
  const leftBound = minPoint.coords[axis].minus(
    l.convertTo(WORKING_UNIT_THICKNESS).scale(0.5)
  )
  const rightBound = minPoint.coords[axis].plus(
    l.convertTo(WORKING_UNIT_THICKNESS).scale(0.5)
  )

  const slices = computeSlices({
    criticalPath,
    startIdx: minPointIdx,
    tc,
    axis,
    fca: fcaMl,
    innerDiameter: d_ml,
    includeSinglePointSlice: true,
  })

  if (slices.length <= 0) return null

  const targetLength = l.toNumber(WORKING_UNIT_THICKNESS)
  const leftBoundNum = leftBound.toNumber(WORKING_UNIT_THICKNESS)
  const rightBoundNum = rightBound.toNumber(WORKING_UNIT_THICKNESS)

  // Filter slices that fit within the averaging window bounds
  const validSlices: ReadonlyArray<CtpSlice> = slices.filter((slice) => {
    const sliceStartPoint = criticalPath[slice.idx_s]
    const sliceEndPoint = criticalPath[slice.idx_e]

    const sliceStartCoord = sliceStartPoint.coords[axis].toNumber(
      WORKING_UNIT_THICKNESS
    )
    const sliceEndCoord = sliceEndPoint.coords[axis].toNumber(
      WORKING_UNIT_THICKNESS
    )

    return (
      sliceStartCoord >= leftBoundNum &&
      sliceEndCoord <= rightBoundNum &&
      slice.si.toNumber(WORKING_UNIT_THICKNESS) <= targetLength
    )
  })

  // Find the slice closest to target length (maximum valid slice)
  const bestSlice: CtpSlice | undefined = minBy(validSlices, (slice) =>
    Math.abs(slice.si.toNumber(WORKING_UNIT_THICKNESS) - targetLength)
  )

  if (isNil(bestSlice)) return null

  const sliceStartIdx = bestSlice.idx_s
  const sliceEndIdx = bestSlice.idx_e

  const leftMostPointInSlice = criticalPath[sliceStartIdx]
  const rightMostPointInSlice = criticalPath[sliceEndIdx]

  let leftArea = 0
  let rightArea = 0

  // Calculate left excess area
  const leftMostX = leftMostPointInSlice.coords[axis].toNumber(
    WORKING_UNIT_THICKNESS
  )

  if (leftBoundNum < leftMostX) {
    const leftNeighborPoint =
      sliceStartIdx > 0 ? criticalPath[sliceStartIdx - 1] : undefined
    leftArea = computeExcessArea(
      leftBoundNum,
      leftMostPointInSlice,
      leftNeighborPoint,
      axis,
      tc
    )
  }

  // Calculate right excess area
  const rightMostX = rightMostPointInSlice.coords[axis].toNumber(
    WORKING_UNIT_THICKNESS
  )

  if (rightBoundNum > rightMostX) {
    const rightNeighborPoint =
      sliceEndIdx < criticalPath.length - 1
        ? criticalPath[sliceEndIdx + 1]
        : undefined
    rightArea = computeExcessArea(
      rightBoundNum,
      rightMostPointInSlice,
      rightNeighborPoint,
      axis,
      tc
    )
  }

  const metalArea = bestSlice.ai0 - bestSlice.ai
  const totalMetalArea = metalArea + leftArea + rightArea

  if (targetLength <= 0) return null

  const averageThickness = totalMetalArea / targetLength

  return GeckoMeasurement.CreateFromValue(
    averageThickness,
    WORKING_UNIT_THICKNESS
  )
}

export interface FfsPartFourCalculatedValues {
  rt: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  setRsfa: Dispatch<SetStateAction<GeckoMeasurement | null>>
  q: GeckoMeasurement | null
  l: GeckoMeasurement | null
  criticalPathCircumferential: ReadonlyArray<CriticalProfilePoint>
  criticalPathLongitudinal: ReadonlyArray<CriticalProfilePoint>
  ts_am: GeckoMeasurement | null
  tc_am: GeckoMeasurement | null
  cdfDataLoading: boolean
}

interface HookProps {
  tmm: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  t_ml: GeckoMeasurement | null
  d_ml: GeckoMeasurement | null
}

export function usePartFourCalculatedValues({
  tmm,
  tc,
  fcaMl,
  t_ml,
  d_ml,
}: HookProps): FfsPartFourCalculatedValues {
  const [rsfa, setRsfa] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(0.9, GeckoMeasurementUnit.Ratio)
  )

  // Get CDF data for critical path computation
  const { relevantThicknessCDF: cdf, indication: indicationItem } =
    useIndicationData()
  const { buffer: cdfData, isLoading: cdfDataLoading } = useCdfRawValues(cdf)

  const rt = useMemo<GeckoMeasurement | null>(() => {
    if (isNil(tmm) || isNil(fcaMl) || isNil(t_ml)) {
      return null
    }

    const numerator =
      tmm.convertTo(WORKING_UNIT_THICKNESS).num -
      fcaMl.convertTo(WORKING_UNIT_THICKNESS).num
    const denominator = t_ml.convertTo(WORKING_UNIT_THICKNESS).num

    if (denominator <= 0) {
      return null
    }

    return GeckoMeasurement.CreateFromValue(
      numerator / denominator,
      GeckoMeasurementUnit.Ratio
    )
  }, [tmm, fcaMl, t_ml])

  // Length Factor Lookup
  const q = useMemo<GeckoMeasurement | null>(() => {
    if (isNil(rt) || isNil(rsfa)) {
      return null
    }

    const qValue = computeLengthFactor(rt.num, rsfa.num)
    return GeckoMeasurement.CreateFromValue(qValue, GeckoMeasurementUnit.Ratio)
  }, [rt, rsfa])

  const l = useMemo<GeckoMeasurement | null>(() => {
    if (isNil(q) || isNil(d_ml) || isNil(t_ml)) {
      return null
    }

    const lValue = computeLength(
      q.num,
      d_ml.convertTo(WORKING_UNIT_THICKNESS).num,
      t_ml.convertTo(WORKING_UNIT_THICKNESS).num
    )
    return GeckoMeasurement.CreateFromValue(lValue, WORKING_UNIT_THICKNESS)
  }, [q, d_ml, t_ml])

  // Critical Path Computation for both axes
  const criticalPathCircumferential: ReadonlyArray<CriticalProfilePoint> =
    useMemo(
      () =>
        cdf && cdfData && indicationItem?.selection && fcaMl
          ? computeCriticalPath(
              indicationItem.selection,
              cdf,
              cdfData.values,
              'x'
            ).map((point) => ({
              ...point,
              value: point.value
                ? GeckoMeasurement.CreateFromValue(
                    point.value.num - fcaMl.toNumber(point.value.unit),
                    point.value.unit
                  )
                : undefined,
            }))
          : [],
      [cdf, cdfData, indicationItem?.selection, fcaMl]
    )

  const criticalPathLongitudinal: ReadonlyArray<CriticalProfilePoint> = useMemo(
    () =>
      cdf && cdfData && indicationItem?.selection && fcaMl
        ? computeCriticalPath(
            indicationItem.selection,
            cdf,
            cdfData.values,
            'y'
          ).map((point) => ({
            ...point,
            value: point.value
              ? GeckoMeasurement.CreateFromValue(
                  point.value.num - fcaMl.toNumber(point.value.unit),
                  point.value.unit
                )
              : undefined,
          }))
        : [],
    [cdf, cdfData, indicationItem?.selection, fcaMl]
  )

  const ts_am = useMemo(
    () =>
      computeCtpAverage(criticalPathCircumferential, 'x', l, tc, fcaMl, d_ml),
    [criticalPathCircumferential, l, tc, fcaMl, d_ml]
  )

  const tc_am = useMemo(
    () => computeCtpAverage(criticalPathLongitudinal, 'y', l, tc, fcaMl, d_ml),
    [criticalPathLongitudinal, l, tc, fcaMl, d_ml]
  )

  return useMemo(
    () => ({
      rt,
      rsfa,
      setRsfa,
      q,
      l,
      criticalPathCircumferential,
      criticalPathLongitudinal,
      ts_am,
      tc_am,
      cdfDataLoading,
    }),
    [
      rt,
      rsfa,
      q,
      l,
      criticalPathCircumferential,
      criticalPathLongitudinal,
      ts_am,
      tc_am,
      cdfDataLoading,
    ]
  )
}

type FfsPartFourCalculatedValuesSectionProps = FfsPartFourCalculatedValues & {
  loading: boolean

  // Values for plot reference lines
  tnom: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  tmm: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
}

const RawFfsPartFourCalculatedValuesSection: FC<
  FfsPartFourCalculatedValuesSectionProps
> = ({
  rt,
  rsfa: rsfa,
  setRsfa,
  q,
  l,
  ts_am,
  tc_am,
  loading,
  criticalPathCircumferential,
  criticalPathLongitudinal,
  tnom,
  trd,
  tc,
  tmm,
  fca,
  fcaMl,
}) => {
  // Modal state
  const [averageCtpModalVisible, setAverageCtpModalVisible] = useState(false)
  const [ctpAxis, setCtpAxis] = useState<'circumferential' | 'longitudinal'>(
    'circumferential'
  )

  const handleShowModal = (direction: 'circumferential' | 'longitudinal') => {
    setCtpAxis(direction)
    setAverageCtpModalVisible(true)
  }

  const handleCloseModal = () => {
    setAverageCtpModalVisible(false)
  }

  // Calculate bounds and min points for both directions
  const circumferentialData = useMemo(() => {
    if (!l || !criticalPathCircumferential.length) return null

    const minIdx = minIndexBy(
      criticalPathCircumferential,
      (point) =>
        point.value?.toNumber(WORKING_UNIT_THICKNESS) ??
        Number.POSITIVE_INFINITY
    )

    if (minIdx < 0) return null

    const minPoint = criticalPathCircumferential[minIdx]
    const leftBound = minPoint.coords.x.minus(l.scale(0.5))
    const rightBound = minPoint.coords.x.plus(l.scale(0.5))

    return { minIdx, leftBound, rightBound }
  }, [criticalPathCircumferential, l])

  const longitudinalData = useMemo(() => {
    if (!l || !criticalPathLongitudinal.length) return null

    const minIdx = minIndexBy(
      criticalPathLongitudinal,
      (point) =>
        point.value?.toNumber(WORKING_UNIT_THICKNESS) ??
        Number.POSITIVE_INFINITY
    )

    if (minIdx < 0) return null

    const minPoint = criticalPathLongitudinal[minIdx]
    const leftBound = minPoint.coords.y.minus(l.scale(0.5))
    const rightBound = minPoint.coords.y.plus(l.scale(0.5))

    return { minIdx, leftBound, rightBound }
  }, [criticalPathLongitudinal, l])

  // Consolidate all modal props based on current axis
  const ctpPlotModalProps = useMemo(() => {
    const isCircumferential = ctpAxis === 'circumferential'
    const data = isCircumferential ? circumferentialData : longitudinalData

    return {
      axis: (isCircumferential ? 'x' : 'y') as 'x' | 'y',
      axisLabel: isCircumferential
        ? 'Circumferential Direction'
        : 'Longitudinal Direction',
      criticalPath: isCircumferential
        ? criticalPathCircumferential
        : criticalPathLongitudinal,
      minPointIndex: data?.minIdx ?? -1,
      leftBound: data?.leftBound ?? null,
      rightBound: data?.rightBound ?? null,
      averageThickness: isCircumferential ? ts_am : tc_am,
    }
  }, [
    ctpAxis,
    circumferentialData,
    longitudinalData,
    criticalPathCircumferential,
    criticalPathLongitudinal,
    ts_am,
    tc_am,
  ])

  return (
    <>
      <Spin spinning={loading}>
        <SectionContainer>
          <FfsParameterField
            label={LABEL_RSFA}
            labelTooltip='Acceptable Remaining Strength Factor'
            labelWidth={LABEL_WIDTH}
            value={rsfa}
            setValue={setRsfa}
            measurementType='ratio'
            min={GeckoMeasurement.CreateFromValue(
              0,
              GeckoMeasurementUnit.Ratio
            )}
            max={GeckoMeasurement.CreateFromValue(
              1,
              GeckoMeasurementUnit.Ratio
            )}
            step={0.05}
          />
          <FfsParameterField
            label={LABEL_R_T}
            labelTooltip={TOOLTIP_R_T}
            labelWidth={LABEL_WIDTH}
            value={rt}
            measurementType='ratio'
          />
          <FfsParameterField
            label={LABEL_Q}
            labelTooltip='Length Factor'
            labelWidth={LABEL_WIDTH}
            value={q}
            measurementType='ratio'
          />
          <FfsParameterField
            label={LABEL_L}
            labelTooltip={
              <>
                Length for thickness averaging
                <TooltipDivider />L = Q × √(D<Sub>ml</Sub> × t<Sub>ml</Sub>)
              </>
            }
            labelWidth={LABEL_WIDTH}
            value={l}
            measurementType='length'
          />
          <FfsParameterField
            label={LABEL_TS_AM}
            labelTooltip='Average measured thickness in circumferential direction'
            labelWidth={LABEL_WIDTH}
            value={ts_am}
            measurementType='thickness'
            onInfo={() => handleShowModal('circumferential')}
          />
          <FfsParameterField
            label={LABEL_TC_AM}
            labelTooltip='Average measured thickness in longitudinal direction'
            labelWidth={LABEL_WIDTH}
            value={tc_am}
            measurementType='thickness'
            onInfo={() => handleShowModal('longitudinal')}
          />
        </SectionContainer>
      </Spin>
      <FfsAverageThicknessModal
        visible={averageCtpModalVisible}
        onClose={handleCloseModal}
        axis={ctpPlotModalProps.axis}
        axisLabel={ctpPlotModalProps.axisLabel}
        criticalPath={ctpPlotModalProps.criticalPath}
        minPointIndex={ctpPlotModalProps.minPointIndex}
        leftBound={ctpPlotModalProps.leftBound}
        rightBound={ctpPlotModalProps.rightBound}
        l={l}
        tnom={tnom}
        trd={trd}
        tc={tc}
        tmm={tmm}
        fca={fca}
        fcaMl={fcaMl}
        averageThickness={ctpPlotModalProps.averageThickness}
      />
    </>
  )
}

export const FfsPartFourCalculatedValuesSection = React.memo(
  RawFfsPartFourCalculatedValuesSection
)
